import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import axios from "axios";
import PersistentSidebarLayout from "../../../../components/PersistentSidebarLayout/PersistentSidebarLayout";
import images from "../../../../utilities/images/images";
import { url_9, url_10 } from "../../../../custom_utilities/api_services";
import userActivityActions from "../../../../redux/user_activity/action";
import "../../../../components/aits/components/Header/PaperListing/PyqTopicWise.css";
import "./SubjectComponentNew.css";

const SubjectComponentNew = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const userPreference = useSelector((state) => state.userPreference);
  const subjectData = useSelector((state) => state.userActivity.subjectData);
  const categoryData = useSelector((state) => state.userActivity.categoryData);
  const isCategorized =
    useSelector((state) => state.userActivity.is_categorized_sub)?.is_categorized_sub === 1;

  const courseId =
    location?.state?.courseId ??
    location?.state?.course_id ??
    userPreference?.courseDetails?.[0]?.course_id;
  const classId = userPreference?.classDetails?.class_id;

  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [activeSubjectId, setActiveSubjectId] = useState(
    Number(subjectData?.subjectId) || null
  );
  const [isSubjectsLoading, setIsSubjectsLoading] = useState(true);
  const [isTopicsLoading, setIsTopicsLoading] = useState(false);

  const visibleSubjects = useMemo(() => {
    if (isCategorized && Array.isArray(categoryData) && categoryData.length) {
      const categoryId = location?.state?.categoryId || subjectData?.categoryId;
      if (categoryId) {
        const selectedCategory = categoryData.find(
          (category) => Number(category.id) === Number(categoryId)
        );
        return selectedCategory?.subject || [];
      }
      return categoryData.flatMap((category) => category.subject || []);
    }
    return subjects;
  }, [
    categoryData,
    isCategorized,
    location?.state?.categoryId,
    subjectData?.categoryId,
    subjects,
  ]);

  const activeSubject = useMemo(
    () =>
      visibleSubjects.find((subject) => Number(subject.sub_id) === Number(activeSubjectId)) ||
      visibleSubjects[0] ||
      null,
    [activeSubjectId, visibleSubjects]
  );

  const fetchSubjects = useCallback(async () => {
    if (!courseId) {
      setIsSubjectsLoading(false);
      return;
    }
    setIsSubjectsLoading(true);
    try {
      const response = await axios.post(url_9, {
        course_id: courseId,
        class_id: classId,
      });
      if (response.data.status === 200) {
        setSubjects(response.data?.data?.subject || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubjectsLoading(false);
    }
  }, [classId, courseId]);

  const fetchTopics = useCallback(
    async (subjectId) => {
      if (!courseId || !subjectId) {
        setTopics([]);
        return;
      }
      setIsTopicsLoading(true);
      try {
        const response = await axios.post(url_10, {
          course_id: courseId,
          subject_id: subjectId,
        });
        if (response.data.status === 200) {
          setTopics(response.data?.data?.topic || []);
        } else {
          setTopics([]);
        }
      } catch (error) {
        console.log(error);
        setTopics([]);
      } finally {
        setIsTopicsLoading(false);
      }
    },
    [courseId]
  );

  useEffect(() => {
    if (!isCategorized) {
      fetchSubjects();
    } else {
      setIsSubjectsLoading(false);
    }
  }, [fetchSubjects, isCategorized]);

  useEffect(() => {
    if (!visibleSubjects.length) return;
    const isCurrentValid = visibleSubjects.some(
      (subject) => Number(subject.sub_id) === Number(activeSubjectId)
    );
    if (!isCurrentValid) {
      setActiveSubjectId(Number(visibleSubjects[0].sub_id));
    }
  }, [activeSubjectId, visibleSubjects]);

  useEffect(() => {
    if (!activeSubject) return;
    dispatch(
      userActivityActions.setSubjectData({
        ...subjectData,
        subjectId: activeSubject.sub_id,
        subjectName: activeSubject.sub_name || activeSubject.name,
        subjectIcon: activeSubject.icon,
      })
    );
    fetchTopics(activeSubject.sub_id);
  }, [activeSubject, dispatch, fetchTopics]);

  const handleBack = () => {
    const returnTo = location?.state?.returnTo;
    const isOldSubject =
      typeof returnTo === "string" && returnTo.includes("/subject_component");
    if (
      typeof returnTo === "string" &&
      returnTo.trim() &&
      returnTo !== location.pathname &&
      !isOldSubject
    ) {
      history.push(returnTo);
      return;
    }
    history.push("/dashboard");
  };

  const handleSubjectChange = (subject) => {
    setActiveSubjectId(Number(subject.sub_id));
  };

  const handleTopicSelect = (topic) => {
    dispatch(
      userActivityActions.setTopicData({
        topicId: topic.topic_id,
        topicName: topic.topic_name,
        topicIcon: topic.icon,
        is_video_available: topic.is_video_available,
        is_live_class_available: topic.is_live_class_available,
        is_excersice_available: topic.is_excersice_available,
        is_notes_available: topic.is_notes_available,
        is_dpp_available: topic.is_dpp_available,
        is_additional_video_available: topic.is_additional_video_available,
        is_pyq_available: topic.is_pyq_available,
      })
    );
    dispatch(userActivityActions.setActiveFeatureType(1));
    history.push({
      pathname: "/topic_component",
      state: {
        courseId,
        featureType: 1,
        fromSubject: true,
        screenVersion: "new",
      },
    });
  };

  return (
    <PersistentSidebarLayout>
      <div className="topic-wise-container subject-component-new">
        <div className="topic-wise-header">
          <img
            className="subject-component-new__back"
            src={images.new_backarrow}
            alt="Back"
            onClick={handleBack}
          />
          <h3 id="practice-wise">Student Learning</h3>
        </div>

        <div className="subject-tabs">
          {visibleSubjects.map((subject) => {
            const isActive = Number(subject.sub_id) === Number(activeSubject?.sub_id);
            return (
              <div
                key={subject.sub_id}
                className={`subject-tab${isActive ? " active" : ""}`}
                onClick={() => handleSubjectChange(subject)}
                style={{
                  color: isActive ? subject.text_color || "#C58900" : "#888",
                }}
              >
                <img src={subject.icon} alt={subject.sub_name} />
                <span>{subject.sub_name}</span>
              </div>
            );
          })}
        </div>

        {isSubjectsLoading || isTopicsLoading ? (
          <div className="subject-component-new__loader">
            <ClipLoader size={40} color="#C61C26" />
          </div>
        ) : (
          <div className="topic-grid">
            {topics.length ? (
              topics.map((topic) => (
                <div
                  className="topic-card"
                  key={topic.topic_id}
                  onClick={() => handleTopicSelect(topic)}
                >
                  <div className="topic-left">
                    <img src={topic.icon} alt={topic.topic_name} />
                    <div className="subject-component-new__topic-meta">
                      <h4>{topic.topic_name}</h4>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="subject-component-new__empty">
                No topics found for this subject.
              </div>
            )}
          </div>
        )}
      </div>
    </PersistentSidebarLayout>
  );
};

export default SubjectComponentNew;
